import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock,
  FileCheck,
  FilePlus,
  Loader2,
  Pencil,
  Receipt,
  RefreshCw,
  Shield,
  ShieldOff,
  StickyNote,
  Trash2,
  User,
  type LucideIcon,
} from "lucide-react";
import {
  usePolicyNotes,
  usePolicyChangeHistory,
  usePolicyDocuments,
  usePolicyActivityLogs,
  usePolicySyncEvents,
  usePolicyRecordUpdates,
  useInsuredCOIHistory,
  useFeatureFlag,
  type NoteRow,
  type PolicyChangeHistoryEntry,
  type ActivityLogWithUser,
  type PolicySyncEvent,
  type COIIssuanceWithUser,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { RecordUpdateType, PolicyRecordUpdateRow } from "@shared/recordUpdate";
import { buildRecordUpdateCard } from "@/lib/record-update-card";
import {
  RecordUpdateModal,
  RECORD_UPDATE_TYPES,
  RECORD_UPDATE_TYPE_LABELS,
} from "./RecordUpdateModal";
import {
  childEntityTitle,
  buildCoiSentLinesFromIssuance,
  acordGeneratedTitle,
  acordGeneratedLines,
} from "@/components/insured/activity-timeline/timeline-events";
import { ACORD_GENERATED_LOG } from "@shared/acord-forms";
import type { AorSource } from "@shared/aor";
import {
  AOR_DETECTED_DESCRIPTION,
  AOR_DETECTED_TITLE,
  aorDetectionTimes,
  aorLogTitle,
  withoutAorChanges,
} from "./aor";
import { additionalInsuredStatusLine, formatActivityAction } from "@/lib/activity-log-format";
import { cn } from "@/lib/utils";
import type { Policy, PolicyDocument } from "@shared/schema";
import type { CoiIssuancePolicySnapshot } from "@shared/coi-template";
import { AddNoteModal } from "@/components/insured/notes/AddNoteModal";
import { EditNoteModal } from "@/components/insured/notes/EditNoteModal";
import { DeleteNoteConfirmModal } from "@/components/insured/notes/DeleteNoteConfirmModal";
import { NotePinButton } from "@/components/insured/notes/NotePinButton";
import {
  localDayKey,
  formatEventTime,
  buildPolicyChangeGroups,
  syncEventTitle,
  syncEventDetail,
  systemSyncActor,
  isSuccessfulSyncOutcome,
  type PolicyContext,
} from "@/lib/policy-activity-formatter";

// CSR-1703: "sync" is the carrier sync-attempt lane (success/failed/not_found/
// removed), sourced from usePolicySyncEvents.
type PolicyActivityKind = "policy" | "notes" | "docs" | "sync";

// CSR-771 — note-event metadata used by ActivityItem to render
// `(edited)` and the AMS-only Edit/Delete icons.
interface PolicyActivityNoteMeta {
  raw: NoteRow;
  isEdited: boolean;
  isAms: boolean;
  // CSR-1885 — global pin state + pin time (ISO).
  pinned: boolean;
  pinnedAt: string | null;
}

export interface PolicyActivityEvent {
  id: string;
  kind: PolicyActivityKind;
  occurredAt: Date;
  title: string;
  description?: string;
  /** CSR-1374: per-field change lines for sync/manual policy edits, shown
   * as an expandable list so no change is hidden behind "+X other changes". */
  changes?: string[];
  performedBy?: string;
  source?: string;
  icon: LucideIcon;
  noteMeta?: PolicyActivityNoteMeta;
}

interface FilterChip {
  key: "all" | PolicyActivityKind;
  label: string;
}

const FILTER_CHIPS: FilterChip[] = [
  { key: "all", label: "All" },
  { key: "notes", label: "Notes" },
  { key: "policy", label: "Policy" },
  { key: "docs", label: "Docs" },
  { key: "sync", label: "Sync" },
];

const KIND_LABEL: Record<PolicyActivityKind, string> = {
  policy: "Policy",
  notes: "Notes",
  docs: "Docs",
  sync: "Sync",
};

const KIND_DOT: Record<PolicyActivityKind, string> = {
  policy: "bg-violet-500",
  notes: "bg-amber-500",
  docs: "bg-teal-500",
  sync: "bg-rose-500",
};

const KIND_ICON_BG: Record<PolicyActivityKind, string> = {
  policy: "bg-violet-500/10 text-violet-600",
  notes: "bg-amber-500/10 text-amber-600",
  docs: "bg-teal-500/10 text-teal-600",
  sync: "bg-rose-500/10 text-rose-600",
};

const KIND_BADGE: Record<PolicyActivityKind, string> = {
  policy: "bg-violet-50 text-violet-700",
  notes: "bg-amber-50 text-amber-700",
  docs: "bg-teal-50 text-teal-700",
  sync: "bg-rose-50 text-rose-700",
};

interface Props {
  policyId: string;
  /** CSR-771: required for cross-surface invalidation. */
  insuredId: string;
  /**
   * CSR-704: carrier identity for source-tag derivation (`PolicyContext`).
   * CSR-1958: the full policy also feeds the Record Update modal's commission
   * pre-fill.
   */
  policy: Policy;
  /** CSR-2327 — the AOR experience flag, read once by the insured page. */
  showAor?: boolean;
}

export function usePolicyActivityEvents({
  policyId,
  insuredId,
  policy,
  showAor = false,
}: Props) {
  const { data: notes = [], isLoading: notesLoading } = usePolicyNotes(policyId);
  const { data: changeHistory = [], isLoading: changeLoading } =
    usePolicyChangeHistory(policyId);
  const { data: documents = [], isLoading: docsLoading } =
    usePolicyDocuments(policyId);
  // CSR-1637 — manual child-entity changes (Additional Insured / Loss Payee /
  // Waiver) that reference this policy, keyed off `details.policyIds`.
  const { data: activityLogsResp, isLoading: activityLoading } =
    usePolicyActivityLogs(policyId);
  const activityLogs = activityLogsResp?.data ?? [];
  const { data: syncEvents = [], isLoading: syncLoading } =
    usePolicySyncEvents(policyId);
  // CSR-1955 — record-update cards (raw deltas suppressed in buildPolicyChangeGroups).
  const { data: recordUpdates = [], isLoading: recordUpdatesLoading } =
    usePolicyRecordUpdates(policyId);
  // The insured's COI issuance history sources the "COI sent" card: directly
  // for issuances covering this policy (post-cutover sends have no AMS-local
  // cert-doc row), and via the CSR-1746 documentId → doc bridge as the legacy
  // fallback.
  const { data: coiIssuances = [], isLoading: coiLoading } =
    useInsuredCOIHistory(insuredId);

  const events = useMemo(
    () =>
      buildPolicyEvents({
        notes,
        changeHistory,
        documents,
        activityLogs,
        syncEvents,
        coiIssuances,
        recordUpdates,
        policy,
        policyId,
        policyNumber: policy.policyNumber,
        showAor,
        aor: {
          identifiedAt: policy.aorIdentifiedAt ?? null,
          source: policy.aorSource ?? null,
        },
      }),
    [
      showAor,
      policy.aorIdentifiedAt,
      policy.aorSource,
      notes,
      changeHistory,
      documents,
      activityLogs,
      syncEvents,
      coiIssuances,
      recordUpdates,
      policy.carrier,
      policy.parentCarrier,
      policy.policyNumber,
      policyId,
    ]
  );

  const isLoading =
    notesLoading ||
    changeLoading ||
    docsLoading ||
    activityLoading ||
    syncLoading ||
    recordUpdatesLoading ||
    coiLoading;

  return { events, isLoading };
}

export function PolicyActivitiesTab({
  policyId,
  insuredId,
  policy,
  showAor = false,
}: Props) {
  const [active, setActive] = useState<FilterChip["key"]>("all");
  const recordUpdateEnabled = useFeatureFlag("feature_flag_record_policy_update");
  const [recordUpdateType, setRecordUpdateType] =
    useState<RecordUpdateType | null>(null);
  const { events, isLoading } = usePolicyActivityEvents({
    policyId,
    insuredId,
    policy,
    showAor,
  });
  const filtered = useMemo(
    () => (active === "all" ? events : events.filter((e) => e.kind === active)),
    [active, events]
  );
  // CSR-1885 — pinned notes float to the very top as bare cards (most recently
  // pinned first), above the day-grouped chronological list. They respect the
  // active filter (partitioned out of `filtered`).
  const pinnedNotes = useMemo(
    () =>
      filtered
        .filter((e) => e.kind === "notes" && e.noteMeta?.pinned)
        .sort((a, b) =>
          (b.noteMeta!.pinnedAt ?? "").localeCompare(a.noteMeta!.pinnedAt ?? ""),
        ),
    [filtered]
  );
  const grouped = useMemo(
    () =>
      groupByDay(
        filtered.filter((e) => !(e.kind === "notes" && e.noteMeta?.pinned)),
      ),
    [filtered]
  );

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="policy-activities">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
            <Clock className="w-5 h-5 text-foreground" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Policy Activity</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Activity scoped to this policy
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div role="tablist" className="flex gap-1.5 flex-wrap">
            {FILTER_CHIPS.map((chip) => {
              const isActive = chip.key === active;
              return (
                <button
                  key={chip.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(chip.key)}
                  className={cn(
                    "px-3 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer",
                    isActive
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                  )}
                  data-testid={`policy-activity-filter-${chip.key}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <AddNoteModal scope="policy" policyId={policyId} insuredId={insuredId} />
          {recordUpdateEnabled && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-full cursor-pointer"
                  data-testid="record-update-button"
                >
                  <FilePlus className="w-4 h-4" /> Record Update
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {RECORD_UPDATE_TYPES.map((type) => (
                  <DropdownMenuItem
                    key={type}
                    onSelect={() => setRecordUpdateType(type)}
                    data-testid={`record-update-option-${type}`}
                  >
                    {RECORD_UPDATE_TYPE_LABELS[type]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {recordUpdateType && (
        <RecordUpdateModal
          type={recordUpdateType}
          open
          onOpenChange={(next) => {
            if (!next) setRecordUpdateType(null);
          }}
          policyId={policyId}
          insuredId={insuredId}
          policy={policy}
        />
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : pinnedNotes.length === 0 && grouped.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No activity yet for this policy.
          </div>
        ) : (
          <div className="space-y-6">
            {pinnedNotes.length > 0 && (
              <div className="space-y-3 border-l border-border pl-5 ml-1.5" data-testid="policy-activity-pinned">
                {pinnedNotes.map((evt) => (
                  <ActivityItem key={evt.id} event={evt} />
                ))}
              </div>
            )}
            {grouped.map((group) => (
              <DaySection key={group.dayKey} day={group.dayLabel} events={group.events} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DaySection({ day, events }: { day: string; events: PolicyActivityEvent[] }) {
  return (
    <section>
      <p className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase mb-3">
        {day}
      </p>
      <div className="space-y-3 border-l border-border pl-5 ml-1.5">
        {events.map((evt) => (
          <ActivityItem key={evt.id} event={evt} />
        ))}
      </div>
    </section>
  );
}

const COLLAPSED_CHANGE_LINES = 3;

function ActivityItem({ event }: { event: PolicyActivityEvent }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const isNote = event.kind === "notes";
  const isAmsNote = isNote && event.noteMeta?.isAms === true;

  const changes = event.changes ?? [];
  const hasHiddenChanges = changes.length > COLLAPSED_CHANGE_LINES;
  const visibleChanges =
    changesExpanded || !hasHiddenChanges
      ? changes
      : changes.slice(0, COLLAPSED_CHANGE_LINES);

  return (
    <article
      className="relative rounded-xl border border-border bg-card p-4"
      data-testid={`policy-activity-${event.id}`}
    >
      <span
        className={cn(
          "absolute -left-[27px] top-5 w-3 h-3 rounded-full ring-4 ring-card",
          KIND_DOT[event.kind]
        )}
        aria-hidden="true"
      />
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
            KIND_ICON_BG[event.kind]
          )}
        >
          <event.icon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider",
                KIND_BADGE[event.kind]
              )}
            >
              {KIND_LABEL[event.kind]}
            </span>
            {event.source && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
                {event.source}
              </span>
            )}
            {isNote && event.noteMeta && (
              <div className="ml-auto flex items-center gap-1">
                {/* CSR-1885 — pin is available on every note card (AMS + NowCerts). */}
                <NotePinButton
                  noteId={event.noteMeta.raw.id}
                  pinned={event.noteMeta.pinned}
                  testId={`policy-activity-note-pin-${event.noteMeta.raw.id}`}
                />
                {isAmsNote && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditOpen(true)}
                      className="p-1 hover:bg-muted rounded cursor-pointer"
                      aria-label="Edit note"
                      data-testid={`policy-activity-note-edit-${event.noteMeta.raw.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteOpen(true)}
                      className="p-1 hover:bg-muted rounded cursor-pointer"
                      aria-label="Delete note"
                      data-testid={`policy-activity-note-delete-${event.noteMeta.raw.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <p className="font-semibold text-sm text-foreground mt-1.5">
            {event.title}
          </p>
          {event.description && (
            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">
              {event.description}
            </p>
          )}
          {changes.length > 0 && (
            <div className="mt-1 space-y-0.5" data-testid={`policy-activity-changes-${event.id}`}>
              {visibleChanges.map((line, index) => (
                <p
                  key={index}
                  className="text-xs text-muted-foreground whitespace-pre-wrap"
                >
                  {line}
                </p>
              ))}
              {hasHiddenChanges && (
                <button
                  type="button"
                  onClick={() => setChangesExpanded((prev) => !prev)}
                  className="text-xs font-medium text-primary hover:underline cursor-pointer"
                  data-testid={`policy-activity-changes-toggle-${event.id}`}
                >
                  {changesExpanded
                    ? "Show less"
                    : `Show ${changes.length - COLLAPSED_CHANGE_LINES} more`}
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" aria-hidden="true" />
              {event.performedBy || "AMS System"}
            </span>
            <span>{formatEventTime(event.occurredAt)}</span>
            {event.kind === "notes" && event.noteMeta?.isEdited && (
              <span data-testid={`policy-activity-note-edited-${event.noteMeta.raw.id}`}>
                (edited)
              </span>
            )}
          </div>
        </div>
      </div>

      {isAmsNote && event.noteMeta && (
        <>
          <EditNoteModal
            note={event.noteMeta.raw}
            open={editOpen}
            onOpenChange={setEditOpen}
          />
          <DeleteNoteConfirmModal
            note={event.noteMeta.raw}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
          />
        </>
      )}
    </article>
  );
}

interface BuildPolicyEventsArgs {
  notes: NoteRow[];
  changeHistory: PolicyChangeHistoryEntry[];
  documents: PolicyDocument[];
  /** CSR-1637: child-entity manual changes referencing this policy. */
  activityLogs: ActivityLogWithUser[];
  syncEvents: PolicySyncEvent[];
  /** Insured COI issuance history — the primary "COI sent" card source
   * (scoped per policy via `coverageLimits.coiPolicies`), and the CSR-1746
   * documentId → issuance enrichment for the doc-bridge fallback. */
  coiIssuances: COIIssuanceWithUser[];
  /** CSR-1955: record-update rows; optional — omitted → no record-update card. */
  recordUpdates?: PolicyRecordUpdateRow[];
  policy: PolicyContext;
  /** Local policy id (`policies[i].id`, e.g. "dc-6257") — matched against the
   * CSR-778 `coverageLimits.coiPolicies[].policyId` lineage, which mints the
   * same `dc-` namespace. */
  policyId: string;
  /** CSR-2064: this policy's human-readable number, for the generated-ACORD
   * card's detail line. Optional — the card degrades to the carrier alone. */
  policyNumber?: string | null;
  /** CSR-2327 — the AOR experience flag and this policy's AOR state. */
  showAor?: boolean;
  aor?: { identifiedAt: string | null; source: AorSource | null } | null;
}

// CSR-2064 — the generated-form row, recognised through the shared constant the
// save endpoint writes and the policy feed's SQL matches.
function isAcordGeneratedLog(log: ActivityLogWithUser): boolean {
  return (
    log.entityType === ACORD_GENERATED_LOG.entityType &&
    log.action === ACORD_GENERATED_LOG.action
  );
}

// `details` is a nullable free-text column, so both readers parse defensively
// and degrade rather than throwing on a row that predates or breaks the shape.
function acordLogDetails(log: ActivityLogWithUser): Record<string, unknown> {
  try {
    const parsed = JSON.parse((log.details as string | null) ?? "");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function acordFormIdFromLog(log: ActivityLogWithUser): string | null {
  const value = acordLogDetails(log).acordFormId;
  return typeof value === "string" ? value : null;
}

function acordPolicyCountFromLog(log: ActivityLogWithUser): number {
  const value = acordLogDetails(log).policyIds;
  return Array.isArray(value) ? value.length : 0;
}

export function buildPolicyEvents({
  notes,
  changeHistory,
  documents,
  activityLogs,
  syncEvents,
  coiIssuances,
  recordUpdates = [],
  policy,
  policyId,
  policyNumber = null,
  showAor = false,
  aor = null,
}: BuildPolicyEventsArgs): PolicyActivityEvent[] {
  const events: PolicyActivityEvent[] = [];

  // CSR-2327 — each automatic AOR detection becomes one event, in place of the
  // raw AOR field-change rows.
  if (showAor) {
    aorDetectionTimes(changeHistory, aor).forEach((occurredAt, i) => {
      events.push({
        id: `aor-detected-${policyId}-${i}`,
        kind: "policy",
        occurredAt,
        title: AOR_DETECTED_TITLE,
        description: AOR_DETECTED_DESCRIPTION,
        performedBy: "System",
        icon: ShieldOff,
      });
    });
    changeHistory = withoutAorChanges(changeHistory);
  }

  // CSR-1637 — Additional Insured / Loss Payee / Waiver of Subrogation manual
  // changes that reference this policy. Rendered under the Policy kind so they
  // sit alongside coverage/field edits and share the Policy filter chip.
  //
  // CSR-2064 — the same feed also carries generated ACORD forms, which are a
  // Docs event rather than a policy change.
  for (const log of activityLogs) {
    const performedBy = log.userName ?? log.userEmail ?? "System";
    const occurredAt = new Date(log.createdAt as unknown as string);

    // CSR-2327 — an agent's flag / "Access restored".
    const aorTitle = aorLogTitle(log);
    if (aorTitle) {
      events.push({
        id: `al-${log.id}`,
        kind: "policy",
        occurredAt,
        title: aorTitle,
        performedBy,
        icon: ShieldOff,
      });
      continue;
    }

    if (isAcordGeneratedLog(log)) {
      events.push({
        id: `al-${log.id}`,
        kind: "docs",
        occurredAt,
        title: acordGeneratedTitle(
          acordFormIdFromLog(log),
          log.entityName ?? "",
        ),
        // This tab knows only the policy being viewed, so the others the same
        // form covered are counted rather than named — spelling them out would
        // mean loading the insured's whole policy list for one line of text.
        changes: acordGeneratedLines(
          [{ policyNumber, carrier: policy.carrier }],
          Math.max(0, acordPolicyCountFromLog(log) - 1),
        ),
        performedBy,
        icon: FileCheck,
      });
      continue;
    }

    // CSR-2274 — only a CSR's status change reaches this tab: the carrier
    // sweep's rows are insured-level and carry no policyIds.
    const statusLine = additionalInsuredStatusLine(log);
    events.push({
      id: `al-${log.id}`,
      kind: "policy",
      occurredAt,
      title: childEntityTitle(log.entityType, log.action, log.entityName),
      ...(statusLine ? { changes: [statusLine] } : {}),
      performedBy,
      icon: User,
    });
  }

  // CSR-771: title and details are first-class columns. The legacy
  // `**Title**\n\nDetails` decoder is gone.
  for (const note of notes) {
    events.push({
      id: `n-${note.id}`,
      kind: "notes",
      occurredAt: new Date(note.createdAt as unknown as string),
      title: note.title,
      description: note.details ?? undefined,
      performedBy: note.authorDisplayNameResolved,
      icon: StickyNote,
      noteMeta: {
        raw: note,
        isEdited: note.isEdited,
        isAms: note.source === "ams",
        pinned: note.pinnedAt != null,
        pinnedAt: (note.pinnedAt as unknown as string) ?? null,
      },
    });
  }

  // CSR-1843 — change-history cards are built by the shared
  // `buildPolicyChangeGroups` so the Insured Overview timeline renders the
  // identical content from the same source.
  for (const group of buildPolicyChangeGroups(changeHistory, policy)) {
    events.push({
      id: `ch-${group.id}`,
      kind: "policy",
      occurredAt: new Date(group.changedAt),
      title: group.title,
      description: group.note ?? undefined,
      changes: group.changeLines,
      source: group.sourceTag,
      performedBy: group.actor ?? undefined,
      icon: Shield,
    });
  }

  // CSR-1955 / CSR-1960 — record-update cards (shared buildRecordUpdateCard).
  for (const row of recordUpdates) {
    const card = buildRecordUpdateCard(row);
    events.push({
      id: `ru-${row.id}`,
      kind: "policy",
      occurredAt: new Date(row.createdAt),
      title: card.title,
      changes: card.changeLines,
      performedBy: card.authorName,
      icon: Receipt,
    });
  }

  // Post-cutover sends (standalone COI DB, 2026-07-29) write the cert PDF row
  // into the COI service's own database, so no AMS-local `policy_documents` row
  // exists to key the CSR-1746 doc-bridge card below. Sent issuances covering
  // this policy — via the CSR-778 `coverageLimits.coiPolicies` lineage, the
  // only durable record of which policies a COI combined — therefore render the
  // "COI sent" card directly, with the same shared line builder as the insured
  // timeline (coiIssuancesToEvents). A multi-policy COI cards on EVERY policy
  // it covered, not just the one hosting the cert PDF.
  const coversThisPolicy = (iss: COIIssuanceWithUser): boolean => {
    const snapshot = (
      iss.coverageLimits as { coiPolicies?: CoiIssuancePolicySnapshot[] } | null
    )?.coiPolicies;
    return (
      Array.isArray(snapshot) && snapshot.some((p) => p?.policyId === policyId)
    );
  };

  for (const iss of coiIssuances) {
    if (iss.deliveryStatus !== "sent" || !coversThisPolicy(iss)) continue;
    const lines = buildCoiSentLinesFromIssuance(iss);
    events.push({
      id: `coi-${iss.id}`,
      kind: "docs",
      occurredAt: new Date((iss.sentAt ?? iss.issuedAt) as unknown as string),
      title: formatActivityAction("coi.sent"), // "COI sent"
      changes: lines.length ? lines : undefined,
      performedBy: iss.issuedByName ?? undefined,
      icon: FileCheck,
    });
  }

  // CSR-1746 — the auto-generated COI PDF is a `policy_documents` row tagged
  // `documentType: "certificate_of_insurance"`. Consolidate it into ONE
  // "COI sent" card (mirrors the insured timeline: docsToEvents drops the cert
  // PDF and coiIssuancesToEvents renders the send), enriched from the mapped
  // issuance via the documentId bridge. It is NOT shown as a raw Docs card.
  // Since the issuance branch above, this is the fallback for rows without a
  // `coiPolicies` snapshot (pre-CSR-778, or a doc hosted on a policy outside
  // the lineage).
  const issuanceByDocId = new Map<string, COIIssuanceWithUser>();
  for (const iss of coiIssuances) {
    if (iss.documentId) issuanceByDocId.set(iss.documentId, iss);
  }

  for (const doc of documents) {
    if (doc.documentType === "certificate_of_insurance") {
      const iss = issuanceByDocId.get(doc.id);
      // Already rendered by the issuance branch — pre-cutover sends have BOTH
      // the local doc row and the issuance. Suppressing only on an exact
      // per-policy match keeps the fallback cases on this branch.
      if (iss && iss.deliveryStatus === "sent" && coversThisPolicy(iss)) {
        continue;
      }
      // A mapped-but-not-sent issuance (processing/failed) shows nothing here;
      // a legacy cert PDF with no mapped issuance is a historical successful
      // send, rendered as a title-only "COI sent" card.
      if (iss && iss.deliveryStatus !== "sent") continue;
      const lines = iss ? buildCoiSentLinesFromIssuance(iss) : [];
      events.push({
        id: `coi-${doc.id}`,
        kind: "docs",
        occurredAt: new Date(
          (iss?.sentAt ?? iss?.issuedAt ?? doc.createdAt) as unknown as string,
        ),
        title: formatActivityAction("coi.sent"), // "COI sent"
        changes: lines.length ? lines : undefined,
        performedBy: iss?.issuedByName ?? undefined,
        icon: FileCheck,
      });
      continue;
    }
    events.push({
      id: `d-${doc.id}`,
      kind: "docs",
      occurredAt: new Date(doc.createdAt as unknown as string),
      title: doc.name,
      description: doc.fileName,
      source: "Manual",
      performedBy: "User",
      icon: FileCheck,
    });
  }

  // CSR-1703 — every manual/scheduled sync attempt (success, failed,
  // not_found, removed) appears in the feed via the local `synced`
  // activity-log rows. CSR-1746 — titled with the Event Logging Spec standard
  // titles (manual vs system derived from `performedBy`), with the failure
  // reason surfaced as the detail line.
  for (const evt of syncEvents) {
    events.push({
      id: `s-${evt.id}`,
      kind: "sync",
      occurredAt: new Date(evt.occurredAt),
      title: syncEventTitle(evt),
      description: syncEventDetail(evt),
      source: "System",
      performedBy: evt.performedBy ?? systemSyncActor(policy),
      // CSR-1977 — a no-change run is healthy; only real failures get the
      // warning triangle (Codex pre-PR review).
      icon: isSuccessfulSyncOutcome(evt.outcome) ? RefreshCw : AlertTriangle,
    });
  }

  events.sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());
  return events;
}

interface DayGroup {
  dayKey: string;
  dayLabel: string;
  events: PolicyActivityEvent[];
}

function groupByDay(events: PolicyActivityEvent[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const evt of events) {
    const dayKey = localDayKey(evt.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.dayKey === dayKey) {
      last.events.push(evt);
    } else {
      groups.push({
        dayKey,
        dayLabel: evt.occurredAt.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        events: [evt],
      });
    }
  }
  return groups;
}
